import { useConst } from '@hmans/use-const';
import * as React from 'react';
import React__default, { createContext, useRef, useContext, useLayoutEffect, useMemo, useImperativeHandle, forwardRef } from 'react';
import * as THREE from 'three';
import { AudioContext as AudioContext$1, AudioLoader } from 'three';
import { makeStore, useStore } from 'statery';
import { useLoader } from '@react-three/fiber';

var AudioNodeContext = /*#__PURE__*/createContext(null);
var AudioContext = function AudioContext(_ref) {
  var children = _ref.children;
  var context = useConst(function () {
    return THREE.AudioContext.getContext();
  });
  return /*#__PURE__*/React__default.createElement(AudioNodeContext.Provider, {
    value: context.destination
  }, children);
};

var store = makeStore({
  listener: null
});

var AudioListener = function AudioListener() {
  var listener = useRef(null);
  var context = useContext(AudioNodeContext);
  useLayoutEffect(function () {
    store.set({
      listener: listener.current
    });
    return function () {
      store.set({
        listener: null
      });
    };
  }, [listener]);
  useLayoutEffect(function () {
    // listener.current.setFilter(context)
    listener.current.gain.connect(context);
    return function () {
      listener.current.gain.disconnect();
    };
  }, [listener, context]);
  return /*#__PURE__*/React__default.createElement("audioListener", {
    ref: listener
  });
};

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

var useAudioComposer = function useAudioComposer() {
  return useStore(store);
};
var useAudioContext = function useAudioContext() {
  return useContext(AudioNodeContext);
};
var useAudioNode = function useAudioNode(ctor, ref, target) {
  var audioCtx = AudioContext$1.getContext();
  var parent = useContext(AudioNodeContext);

  /* Create node */
  var node = useMemo(function () {
    return ctor(audioCtx);
  }, []);

  /* Wire up to parent */
  useLayoutEffect(function () {
    var t = target ? parent[target] : parent;
    node.connect(t);
    return function () {
      return node.disconnect(t);
    };
  }, [node, parent, target]);
  useImperativeHandle(ref, function () {
    return node;
  });
  return node;
};

var _excluded$1 = ["children"];
var Compressor = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children;
    _objectWithoutProperties(_ref, _excluded$1);
  var node = useAudioNode(function (ctx) {
    return ctx.createDynamicsCompressor();
  }, ref);
  var t = node.context.currentTime;
  node.threshold.setValueAtTime(-50, t);
  node.knee.setValueAtTime(40, t);
  node.ratio.setValueAtTime(12, t);
  node.attack.setValueAtTime(0, t);
  node.release.setValueAtTime(0.25, t);
  return /*#__PURE__*/React__default.createElement(AudioNodeContext.Provider, {
    value: node
  }, children);
});

var Filter = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? "allpass" : _ref$type,
    _ref$frequency = _ref.frequency,
    frequency = _ref$frequency === void 0 ? 2000 : _ref$frequency,
    Q = _ref.Q;
  var node = useAudioNode(function (ctx) {
    return ctx.createBiquadFilter();
  }, ref);
  useLayoutEffect(function () {
    node.type = type;
  }, [node, type]);
  useLayoutEffect(function () {
    node.frequency.setTargetAtTime(frequency, node.context.currentTime, 0.1);
  }, [node, frequency]);
  useLayoutEffect(function () {
    if (Q !== undefined) node.Q.value = Q;
  }, [node, Q]);
  return /*#__PURE__*/React__default.createElement(AudioNodeContext.Provider, {
    value: node
  }, children);
});

var Gain = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$volume = _ref.volume,
    volume = _ref$volume === void 0 ? 0.5 : _ref$volume,
    children = _ref.children,
    target = _ref.target;
  var node = useAudioNode(function (ctx) {
    return ctx.createGain();
  }, ref, target);
  useLayoutEffect(function () {
    node.gain.value = volume;
  }, [volume]);
  return /*#__PURE__*/React__default.createElement(AudioNodeContext.Provider, {
    value: node
  }, children);
});

var LinearRamp = function LinearRamp(_ref) {
  var to = _ref.to,
    duration = _ref.duration,
    property = _ref.property;
  var parent = useAudioContext();
  useLayoutEffect(function () {
    if (!parent) return;
    var time = parent.context.currentTime;
    var prop = parent[property];
    if (!prop) {
      console.error("Invalid property", property);
      return;
    }
    prop.setValueAtTime(prop.value, time);
    prop.linearRampToValueAtTime(to, time + duration);
  }, [parent]);
  return null;
};

var Oscillator = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$type = _ref.type,
    type = _ref$type === void 0 ? "sine" : _ref$type,
    _ref$frequency = _ref.frequency,
    frequency = _ref$frequency === void 0 ? 440 : _ref$frequency,
    _ref$detune = _ref.detune,
    detune = _ref$detune === void 0 ? 0 : _ref$detune,
    _ref$start = _ref.start,
    start = _ref$start === void 0 ? 0 : _ref$start,
    duration = _ref.duration,
    target = _ref.target,
    children = _ref.children;
  var node = useAudioNode(function (ctx) {
    return ctx.createOscillator();
  }, ref, target);

  /* Apply props */
  node.type = type;
  node.frequency.value = frequency;
  node.detune.value = detune;
  useLayoutEffect(function () {
    var t = node.context.currentTime;
    if (typeof start === "number") node.start(t + start);
    if (typeof duration === "number") node.stop(t + start + duration);
    return function () {
      return node.stop();
    };
  }, [node]);
  return /*#__PURE__*/React__default.createElement(AudioNodeContext.Provider, {
    value: node
  }, children);
});

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

var _excluded = ["url", "distance", "volume", "rate", "loop", "autoplay"];
var PositionalAudio = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var url = _ref.url,
    _ref$distance = _ref.distance,
    distance = _ref$distance === void 0 ? 1 : _ref$distance,
    _ref$volume = _ref.volume,
    volume = _ref$volume === void 0 ? 1 : _ref$volume,
    _ref$rate = _ref.rate,
    rate = _ref$rate === void 0 ? 1 : _ref$rate,
    _ref$loop = _ref.loop,
    loop = _ref$loop === void 0 ? false : _ref$loop,
    _ref$autoplay = _ref.autoplay,
    autoplay = _ref$autoplay === void 0 ? true : _ref$autoplay,
    props = _objectWithoutProperties(_ref, _excluded);
  var sound = React.useRef(null);
  var buffer = useLoader(AudioLoader, url);
  var _useStore = useStore(store),
    listener = _useStore.listener;
  React.useEffect(function () {
    if (!sound.current || !buffer) return;
    sound.current.setBuffer(buffer);
    sound.current.setRefDistance(distance);
    sound.current.setLoop(loop);
    sound.current.setVolume(volume);
    sound.current.setPlaybackRate(rate);
    if (autoplay && !sound.current.isPlaying) {
      sound.current.play();
    }
  }, [buffer, distance, loop, listener]);
  React.useImperativeHandle(ref, function () {
    return sound.current;
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, listener && /*#__PURE__*/React.createElement("positionalAudio", _extends({
    ref: sound,
    args: [listener]
  }, props)));
});

var Reverb = /*#__PURE__*/forwardRef(function (_ref, ref) {
  _ref.wet;
    var _ref$seconds = _ref.seconds,
    seconds = _ref$seconds === void 0 ? 3 : _ref$seconds,
    _ref$decay = _ref.decay,
    decay = _ref$decay === void 0 ? 2 : _ref$decay,
    _ref$reverse = _ref.reverse,
    reverse = _ref$reverse === void 0 ? false : _ref$reverse,
    children = _ref.children;
  var node = useAudioNode(function (ctx) {
    return ctx.createConvolver();
  }, ref);

  /* Build the reverb buffer */
  useLayoutEffect(function () {
    var rate = node.context.sampleRate,
      length = rate * seconds,
      impulse = node.context.createBuffer(2, length, rate),
      impulseL = impulse.getChannelData(0),
      impulseR = impulse.getChannelData(1);
    for (var i = 0; i < length; i++) {
      var n = reverse ? length - i : i;
      impulseL[i] = (Math.random() * 2 - 1) * Math.pow(1 - n / length, decay);
      impulseR[i] = (Math.random() * 2 - 1) * Math.pow(1 - n / length, decay);
    }
    node.buffer = impulse;
  }, [seconds, decay, reverse]);

  /* For now, also connect the input to our own output */
  var input = useMemo(function () {
    var input = node.context.createGain();
    input.connect(node);
    return input;
  }, [node]);

  /* Connect input to parent, too */
  var parent = useAudioContext();
  useLayoutEffect(function () {
    input.connect(parent);
  }, [input, parent]);
  return /*#__PURE__*/React__default.createElement(AudioNodeContext.Provider, {
    value: input
  }, children);
});

export { AudioContext, AudioListener, AudioNodeContext, Compressor, Filter, Gain, LinearRamp, Oscillator, PositionalAudio, Reverb, useAudioComposer, useAudioContext, useAudioNode };
